// A lista de mensagens.
//
// DUAS COISAS que parecem uma só e não são:
//
//   paginação por CURSOR  — de onde vêm as próximas mensagens (rede)
//   virtualização         — quantas linhas existem no DOM (navegador)
//
// A primeira existe porque a caixa RECEBE enquanto o usuário navega: paginar
// por deslocamento faria mensagens pularem ou repetirem entre páginas. A
// segunda existe porque uma caixa com 40 mil mensagens, depois de rolar um
// pouco, teria 40 mil linhas no DOM e travaria o navegador.

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import type { MensagemResumo } from "../api/tipos";
import { Icone } from "../ui/Icone";
import { corDoAvatar, dataCurta, inicial, nomeCurto } from "../ui/formato";
import { useCelular } from "../ui/tela";
import { Etiqueta, apenasDoUsuario } from "./Marcadores";
import { BarraDeSelecao } from "./Selecao";

// No desktop cabe tudo numa linha; no celular, não. Remetente, assunto e
// prévia espremidos em 44px viram três reticências lado a lado, e a pessoa
// não consegue decidir o que abrir — que é a única função desta tela.
const ALTURA_LINHA = 44;
const ALTURA_LINHA_CELULAR = 76;

export function useListaDaPasta(pasta: string) {
  return useInfiniteQuery({
    queryKey: ["mensagens", pasta],
    // A LISTA SE ATUALIZA SOZINHA.
    //
    // Faltava isto, e era metade do "demora até um minuto para a mensagem
    // aparecer": o índice passou a ser atualizado em ~1s pela vigília IMAP
    // IDLE, mas a tela só refazia a lista ao trocar de pasta, ao voltar o
    // foco da janela, ou no botão de atualizar. A mensagem estava lá e
    // ninguém ia buscar.
    //
    // 10s é o teto do que a pessoa espera, não o custo: a consulta é no
    // ÍNDICE, não no IMAP. E o react-query não roda o intervalo com a janela
    // em segundo plano, então aba esquecida aberta não consulta nada.
    refetchInterval: 10_000,
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      correio.listar({ pasta, limite: 50, cursor: pageParam || undefined }, signal),
    // `undefined` encerra a rolagem infinita. Devolver "" faria o react-query
    // pedir a mesma primeira página para sempre.
    getNextPageParam: (ultima) => (ultima.tem_mais ? ultima.proximo_cursor || undefined : undefined),
  });
}

/**
 * Quem a linha mostra: quem mandou, ou para quem foi.
 *
 * Em Enviados e Rascunhos o remetente é SEMPRE a própria pessoa — a coluna
 * inteira dizia "suporte", linha após linha, e não ajudava a achar nada. O
 * que identifica uma mensagem que você mandou é PARA QUEM ela foi.
 *
 * As pastas são as canônicas do IMAP, não o rótulo traduzido: o nome do
 * IMAP é estável e o rótulo muda com o idioma.
 */
export function pessoaDaLinha(
  pasta: string,
  m: {
    remetente?: string | null;
    remetente_nome?: string | null;
    destinatarios?: string[] | null;
  },
): { endereco: string; nome: string | null; prefixo: string } {
  if (pasta === "Sent" || pasta === "Drafts") {
    const para = (m.destinatarios ?? []).filter(Boolean);
    if (para.length > 0) {
      return {
        endereco: para[0]!,
        nome: null,
        // "Para:" aparece porque, sem ele, a coluna some de significado: a
        // pessoa não sabe se aquele endereço mandou ou recebeu.
        prefixo: para.length > 1 ? `Para (${para.length}):` : "Para:",
      };
    }
    // Rascunho ainda sem destinatário é estado legítimo.
    return { endereco: "", nome: "(sem destinatário)", prefixo: "" };
  }
  return {
    endereco: m.remetente ?? "",
    nome: m.remetente_nome ?? null,
    prefixo: "",
  };
}

export function Lista() {
  const { pasta = "INBOX" } = useParams();
  const consulta = useListaDaPasta(pasta);
  const fila = useQueryClient();

  // SELEÇÃO MÚLTIPLA.
  //
  // Pedido de usuário: clicar numa linha ABRE a mensagem, então não havia
  // gesto nenhum para agir sobre várias. A caixinha é um alvo PRÓPRIO,
  // dentro da linha mas fora do link — é isso que deixa os dois gestos
  // conviverem.
  //
  // `Set` e não array: a lista é virtualizada e cada quadro de rolagem
  // consulta "esta está selecionada?" para cada linha visível. Com array
  // isso seria uma varredura por linha, por quadro.
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  // A seleção não sobrevive à troca de pasta: agir sobre mensagens que não
  // estão mais na tela é o caminho mais curto para alguém apagar o que não
  // queria.
  useEffect(() => setSelecionadas(new Set()), [pasta]);

  function alternarSelecao(uuid: string) {
    setSelecionadas((atual) => {
      const novo = new Set(atual);
      if (novo.has(uuid)) novo.delete(uuid);
      else novo.add(uuid);
      return novo;
    });
  }

  const mensagens: MensagemResumo[] =
    consulta.data?.pages.flatMap((p) => p.itens ?? []) ?? [];

  const celular = useCelular();
  const alturaLinha = celular ? ALTURA_LINHA_CELULAR : ALTURA_LINHA;

  const caixa = useRef<HTMLDivElement>(null);
  const virtual = useVirtualizer({
    count: mensagens.length,
    getScrollElement: () => caixa.current,
    estimateSize: () => alturaLinha,
    // Algumas linhas além da janela: sem isto, rolar rápido mostra espaço em
    // branco antes de a próxima linha ser desenhada.
    overscan: 12,
  });

  // Girar o aparelho muda a altura da linha, e o virtualizador guarda as
  // posições que já calculou. Sem remedir, a lista fica com buracos entre as
  // linhas ou com uma por cima da outra.
  //
  // A dependência é SÓ a altura. Pôr o virtualizador junto — que é o que a
  // regra do lint pede — cria laço infinito: `measure()` provoca re-render,
  // o re-render dispara o efeito, e o efeito chama `measure()` de novo. Na
  // prática a aba TRAVA, sem erro no console: só para de responder.
  const remedir = useRef(virtual.measure);
  remedir.current = virtual.measure;
  useEffect(() => {
    remedir.current();
  }, [alturaLinha]);

  // Busca a próxima página quando a rolagem se aproxima do fim. O gatilho é a
  // última linha VIRTUAL, não um sentinela no DOM — com virtualização, um
  // sentinela no fim da lista nunca é renderizado e nunca dispara.
  const itens = virtual.getVirtualItems();
  useEffect(() => {
    const ultima = itens[itens.length - 1];
    if (!ultima) return;
    if (ultima.index >= mensagens.length - 10 && consulta.hasNextPage && !consulta.isFetchingNextPage) {
      void consulta.fetchNextPage();
    }
  }, [itens, mensagens.length, consulta]);

  function alternarMarcada(m: MensagemResumo, evento: React.MouseEvent) {
    evento.preventDefault();
    evento.stopPropagation();
    void correio
      .marcar(m.uuid!, { marcada: !m.marcada })
      .then(() => fila.invalidateQueries({ queryKey: ["mensagens", pasta] }));
  }

  if (consulta.isError) {
    return <Aviso icone="AlertTriangle" texto={explicar(consulta.error)} />;
  }
  if (consulta.isPending) {
    return <Aviso icone="RefreshCw" texto="Carregando…" />;
  }
  if (mensagens.length === 0) {
    return <Aviso icone="MailOpen" texto="Nenhuma mensagem nesta pasta." />;
  }

  return (
    <div className="flex-1 flex flex-col bg-white rounded-tl-2xl overflow-hidden min-w-0">
      {selecionadas.size > 0 ? (
        <BarraDeSelecao
          pasta={pasta}
          selecionadas={selecionadas}
          carregadas={mensagens.length}
          aoSelecionarTodas={() => setSelecionadas(new Set(mensagens.map((m) => m.uuid!)))}
          // MESMA FONTE do menu de uma mensagem só, em Leitura.tsx: os
          // marcadores que já estão em uso nas mensagens carregadas. Não há
          // "lista de marcadores" no servidor — eles são keywords do IMAP, e
          // existe o que estiver em uso.
          sugestoes={[
            ...new Set(mensagens.flatMap((m) => apenasDoUsuario(m.marcadores))),
          ].sort()}
          aoLimpar={() => setSelecionadas(new Set())}
          aoTerminar={() => {
            setSelecionadas(new Set());
            void fila.invalidateQueries({ queryKey: ["mensagens", pasta] });
            void fila.invalidateQueries({ queryKey: ["pastas"] });
          }}
        />
      ) : (
        <div className="flex items-center gap-1 px-3 py-2 border-b border-linha-suave text-tinta-media">
          <button
            onClick={() => void consulta.refetch()}
            className="p-2 rounded-full hover:bg-fundo"
            title="Atualizar"
            aria-label="Atualizar"
          >
            <Icone nome="RefreshCw" tamanho={18} />
          </button>

          {/* ESTAR EM "ENVIADOS" NÃO QUER DIZER QUE CHEGOU.
              Esta pasta responde "saiu daqui" e nada mais — o que aconteceu
              depois (aceita, adiada, recusada) está em `cdw.fila_eventos` e
              tem tela própria desde 30/10/2026. A revisão de UX apontou a
              falta como "Enviados sem status de entrega", e o conserto não é
              pôr um status por linha: seria uma consulta de entregas por
              mensagem, e a resposta mais útil é a da tela que já existe, que
              agrupa as tentativas e explica cada recusa em português.
              O que faltava era o caminho até ela, justamente de onde a
              pergunta nasce. */}
          {pasta === "Sent" && (
            <Link
              to="/correio/meu-email-chegou"
              className="flex items-center gap-1.5 rounded-full border border-linha px-2.5 py-1 text-xs text-acao hover:bg-acao-clara"
            >
              <Icone nome="ShieldCheck" tamanho={14} />
              <span className="hidden sm:inline">Chegou ao destino?</span>
              <span className="sm:hidden">Chegou?</span>
            </Link>
          )}

          <span className="ml-auto text-xs tabular-nums">
            {mensagens.length}
            {consulta.hasNextPage ? "+" : ""}{" "}
            {/* Concordância: "1 mensagens" é o tipo de erro que faz a tela
                parecer feita às pressas, e aparece justamente na caixa
                quase vazia — a primeira que alguém novo abre. O plural
                vale também para o "+": "1+ mensagens" está certo. */}
            {mensagens.length === 1 && !consulta.hasNextPage ? "mensagem" : "mensagens"}
          </span>
        </div>
      )}

      <div ref={caixa} className="flex-1 overflow-y-auto">
        <div style={{ height: virtual.getTotalSize(), position: "relative" }}>
          {itens.map((linha) => {
            const m = mensagens[linha.index];
            if (!m) return null;
            return (
              <Link
                key={m.uuid}
                to={`/correio/${encodeURIComponent(pasta)}/${m.uuid}`}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: linha.size,
                  transform: `translateY(${linha.start}px)`,
                }}
                className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 text-sm border-b border-linha-suave hover:shadow-md ${
                  selecionadas.has(m.uuid!)
                    ? "bg-acao-clara text-tinta"
                    : m.lida
                      ? "bg-fundo text-tinta-media"
                      : "bg-white font-semibold text-tinta"
                }`}
              >
                {/* A CAIXINHA É UM ALVO PRÓPRIO, dentro da linha e fora do
                    que abre a mensagem. `preventDefault` no link é o que
                    permite os dois gestos conviverem: clicar na linha abre,
                    clicar aqui seleciona. */}
                <span
                  role="checkbox"
                  tabIndex={0}
                  aria-checked={selecionadas.has(m.uuid!)}
                  aria-label={`Selecionar mensagem de ${nomeCurto(pessoaDaLinha(pasta, m).nome, pessoaDaLinha(pasta, m).endereco)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    alternarSelecao(m.uuid!);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      alternarSelecao(m.uuid!);
                    }
                  }}
                  className="p-2 sm:p-1 rounded-full hover:bg-linha flex-shrink-0 cursor-pointer"
                >
                  <Icone
                    nome={selecionadas.has(m.uuid!) ? "CheckSquare" : "Square"}
                    tamanho={16}
                    className={selecionadas.has(m.uuid!) ? "text-acao" : "text-tinta-fraca"}
                  />
                </span>

                <button
                  onClick={(e) => alternarMarcada(m, e)}
                  // p-2 no celular: 16px de ícone dentro de um alvo de ~32px
                  // é o mínimo para o dedo não abrir a mensagem sem querer ao
                  // tentar estrelar.
                  className="p-2 sm:p-1 rounded-full hover:bg-linha flex-shrink-0"
                  aria-label={m.marcada ? "Tirar a estrela" : "Marcar com estrela"}
                >
                  <Icone
                    nome="Star"
                    tamanho={16}
                    className={m.marcada ? "text-aviso" : "text-tinta-fraca"}
                  />
                </button>

                <span
                  className="h-8 w-8 sm:h-6 sm:w-6 rounded-full grid place-items-center text-white text-xs sm:text-[11px] font-semibold flex-shrink-0"
                  style={{ background: corDoAvatar(pessoaDaLinha(pasta, m).endereco) }}
                  aria-hidden="true"
                >
                  {inicial(pessoaDaLinha(pasta, m).nome, pessoaDaLinha(pasta, m).endereco)}
                </span>

                {celular ? (
                  // Duas linhas, como todo aplicativo de e-mail de celular:
                  // quem mandou e quando em cima, assunto e prévia embaixo.
                  // A data sobe para a primeira linha porque é o que decide
                  // "isto é de agora ou de semana passada" numa olhada.
                  <span className="min-w-0 flex-1 py-2">
                    <span className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate">
                        {pessoaDaLinha(pasta, m).prefixo && (
                          <span className="text-tinta-fraca">
                            {pessoaDaLinha(pasta, m).prefixo}{" "}
                          </span>
                        )}
                        {nomeCurto(
                          pessoaDaLinha(pasta, m).nome,
                          pessoaDaLinha(pasta, m).endereco,
                        )}
                      </span>
                      {m.tem_anexo && (
                        <Icone nome="Paperclip" tamanho={13} className="text-tinta-fraca flex-shrink-0" />
                      )}
                      <span className="flex-shrink-0 text-xs tabular-nums text-tinta-fraca">
                        {dataCurta(m.data)}
                      </span>
                    </span>
                    <span className="block truncate">
                      {apenasDoUsuario(m.marcadores).map((r) => (
                        <Etiqueta key={r} nome={r} />
                      ))}
                      {m.assunto || "(sem assunto)"}
                    </span>
                    <span className="block truncate text-xs font-normal text-tinta-fraca">
                      {m.preview}
                    </span>
                  </span>
                ) : (
                  <>
                    <span className="w-44 truncate flex-shrink-0">
                      {pessoaDaLinha(pasta, m).prefixo && (
                        <span className="text-tinta-fraca">
                          {pessoaDaLinha(pasta, m).prefixo}{" "}
                        </span>
                      )}
                      {nomeCurto(
                        pessoaDaLinha(pasta, m).nome,
                        pessoaDaLinha(pasta, m).endereco,
                      )}
                    </span>

                    <span className="flex-1 truncate min-w-0">
                      {apenasDoUsuario(m.marcadores).map((r) => (
                        <Etiqueta key={r} nome={r} />
                      ))}
                      {m.assunto || "(sem assunto)"}
                      <span className="font-normal text-tinta-fraca"> – {m.preview}</span>
                    </span>

                    {m.tem_anexo && (
                      <Icone nome="Paperclip" tamanho={14} className="text-tinta-fraca flex-shrink-0" />
                    )}
                    <span className="w-16 text-right text-xs tabular-nums flex-shrink-0">
                      {dataCurta(m.data)}
                    </span>
                  </>
                )}
              </Link>
            );
          })}
        </div>
        {consulta.isFetchingNextPage && (
          <p className="py-3 text-center text-xs text-tinta-fraca">carregando mais…</p>
        )}
      </div>
    </div>
  );
}

function Aviso({ icone, texto }: { icone: Parameters<typeof Icone>[0]["nome"]; texto: string }) {
  return (
    <div className="flex-1 bg-white rounded-tl-2xl grid place-items-center text-tinta-fraca">
      <p className="flex items-center gap-2 text-sm">
        <Icone nome={icone} tamanho={18} />
        {texto}
      </p>
    </div>
  );
}
